import { useState, useEffect, useCallback } from 'react'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface AuditLog {
  id: string
  actor_id: string | null
  actor_email: string
  action: string
  entity_type: string
  entity_id: string | null
  entity_repr: string | null
  old_values: Record<string, unknown> | null
  new_values: Record<string, unknown> | null
  changed_fields: Record<string, unknown> | null
  risk_level: string
  ip_address: string | null
  created_at: string
}

interface AuditLogResponse {
  audit_logs: AuditLog[]
  total: number
  has_more: boolean
}

export function SuperadminAuditLogPage() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [actionFilter, setActionFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const limit = 50

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        skip: String(page * limit),
        limit: String(limit),
      })
      if (actionFilter) params.set('action_filter', actionFilter)
      if (dateFrom) params.set('date_from', dateFrom)
      if (dateTo) params.set('date_to', dateTo)

      const res = await handleApiRequest<AuditLogResponse>(apiClient.get(`/api/v1/admin/audit-logs?${params}`))
      setLogs(res.audit_logs)
      setTotal(res.total)
      setError(null)
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Audit-Logs konnten nicht geladen werden' }))
    } finally { setLoading(false) }
  }, [page, actionFilter, dateFrom, dateTo])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  const riskColor = (level: string) => {
    switch (level) {
      case 'HIGH': return 'bg-red-100 text-red-700'
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-700'
      default: return 'bg-blue-100 text-blue-700'
    }
  }

  const formatDate = (d: string) => {
    try { return new Date(d).toLocaleString('de-CH') } catch { return d }
  }

  const handleExport = () => {
    const csv = ['Zeit,Aktion,Typ,Actor,Entity,Risk,IP'].concat(
      logs.map(l => `${l.created_at},${l.action},${l.entity_type},${l.actor_email},${l.entity_repr || ''},${l.risk_level},${l.ip_address || ''}`)
    ).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'audit-log.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Audit-Log</h1>
        <button onClick={handleExport} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
          CSV Export
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4 mb-4">
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Aktion</label>
            <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0) }}
              className="w-full px-3 py-2 border rounded-lg text-sm">
              <option value="">Alle</option>
              <option value="ROLE_CHANGE">Rollen-Änderung</option>
              <option value="TEACHER_CREATE">Lehrer erstellt</option>
              <option value="TEACHER_UPDATE">Lehrer aktualisiert</option>
              <option value="TEACHER_DELETE">Lehrer geloescht</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Datum von</label>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0) }}
              className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Datum bis</label>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0) }}
              className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={() => { setActionFilter(''); setDateFrom(''); setDateTo(''); setPage(0) }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
              Filter zuruecksetzen
            </button>
          </div>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      {loading ? (
        <div className="p-6 text-center text-gray-500">Laden...</div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-lg border p-8 text-center text-gray-500">Keine Audit-Logs gefunden</div>
      ) : (
        <>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zeit</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktion</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map(log => (
                  <>
                    <tr key={log.id} className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
                      <td className="px-4 py-3">
                        {(log.old_values || log.new_values || log.changed_fields) ? (
                          expandedId === log.id ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-medium text-gray-900">{log.action}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="font-medium">{log.entity_type}</div>
                        <div className="text-xs text-gray-400">{log.entity_repr || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{log.actor_email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${riskColor(log.risk_level)}`}>
                          {log.risk_level}
                        </span>
                      </td>
                    </tr>
                    {expandedId === log.id && (log.old_values || log.new_values || log.changed_fields) && (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-2 gap-4">
                            {log.old_values && (
                              <div>
                                <h4 className="text-xs font-medium text-red-600 mb-2">Vorher</h4>
                                <pre className="text-xs bg-red-50 border border-red-200 rounded p-3 overflow-auto max-h-40">
                                  {JSON.stringify(log.old_values, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log.new_values && (
                              <div>
                                <h4 className="text-xs font-medium text-green-600 mb-2">Nachher</h4>
                                <pre className="text-xs bg-green-50 border border-green-200 rounded p-3 overflow-auto max-h-40">
                                  {JSON.stringify(log.new_values, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log.changed_fields && !log.old_values && (
                              <div className="col-span-2">
                                <h4 className="text-xs font-medium text-gray-600 mb-2">Änderungen</h4>
                                <pre className="text-xs bg-gray-100 border rounded p-3 overflow-auto max-h-40">
                                  {JSON.stringify(log.changed_fields, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                          {log.ip_address && <div className="text-xs text-gray-400 mt-2">IP: {log.ip_address}</div>}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              {page * limit + 1} - {Math.min((page + 1) * limit, total)} von {total}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1.5 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">
                Zurueck
              </button>
              <button onClick={() => setPage(p => p + 1)} disabled={!logs.length || logs.length < limit}
                className="px-3 py-1.5 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">
                Weiter
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
